import { useEffect, useRef, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import adminService from "../../api/services/adminService";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { UserRound, Home, Car } from "lucide-react";
import { useAppConfigContext } from "../../context/AppConfigContext";
import { formatCurrency as formatCurrencyValue } from "../../utils/currency";

// Mock data for revenue performance chart
const weeklyRevenueData = [
  { label: "Monday", revenue: 45 },
  { label: "Tuesday", revenue: 30 },
  { label: "Wednesday", revenue: 60 },
  { label: "Thursday", revenue: 40 },
  { label: "Friday", revenue: 55 },
  { label: "Saturday", revenue: 12 },
  { label: "Sunday", revenue: 90 },
];

const kpiCards = [
  {
    title: "Total Revenue",
    value: "N50,046,000",
    change: "+5.2%",
    segments: [
      { label: "Trips", color: "#570033", width: "40%" },
      { label: "Rides", color: "#9ca3af", width: "30%" },
      { label: "Stay", color: "#570033", width: "30%" },
    ],
  },
  {
    title: "Total Bookings",
    value: "2,045",
    change: "+12%",
    segments: [
      { label: "Trips", color: "#570033", width: "40%" },
      { label: "Rides", color: "#9ca3af", width: "30%" },
      { label: "Stay", color: "#570033", width: "30%" },
    ],
  },
  {
    title: "Active Users",
    value: "15,000",
    change: "+3%",
    segments: [
      { label: "Returning User", color: "#570033", width: "70%" },
      { label: "New User", color: "#9ca3af", width: "30%" },
    ],
  },
  {
    title: "New Registrations",
    value: "+120",
    change: "+8%",
    progress: { width: "80%", label: "Target: 150/week" },
  },
];

const buildUrgentItems = ({ overviewData, providersMetrics, isLoading }) => {
  if (isLoading) {
    return [
      {
        title: "Loading alerts",
        message: "Fetching the latest urgent items.",
        action: "Please wait",
        tone: "info",
        icon: "🛡️",
        actionColor: "#61013B",
      },
    ];
  }

  const items = [];
  const totals = overviewData?.totals ?? overviewData ?? {};
  const providersSource = providersMetrics?.totals ?? providersMetrics?.metrics ?? providersMetrics ?? {};

  const bookingsMetric =
    totals.totalBookings ||
    totals.total_bookings ||
    totals.bookings ||
    totals.totalBookingsCount ||
    totals.total_bookings_count;
  const bookingsChange = getMetricChange(bookingsMetric);
  if (typeof bookingsChange === "number" && bookingsChange <= -10) {
    items.push({
      title: "Bookings Down",
      message: `Bookings are down ${Math.abs(bookingsChange).toFixed(2)}% vs last month.`,
      action: "Review Bookings",
      tone: "danger",
      icon: "⚠️",
      actionColor: "#FF0000",
    });
  }

  const registrationsMetric =
    totals.totalNewRegistrations ||
    totals.newRegistrations ||
    totals.new_registrations ||
    totals.newUsers ||
    totals.new_users ||
    totals.newUserRegistrations ||
    totals.new_user_registrations;
  const registrationsChange = getMetricChange(registrationsMetric);
  if (typeof registrationsChange === "number" && registrationsChange <= -10) {
    items.push({
      title: "New Users Down",
      message: `New registrations are down ${Math.abs(registrationsChange).toFixed(2)}% vs last month.`,
      action: "View Growth",
      tone: "warning",
      icon: "🛡️",
      actionColor: "#F99C07",
    });
  }

  const pendingVerification = getFirstValue(providersSource, [
    "pendingVerification",
    "pending_verification",
    "pendingProfiles",
    "pending_profiles",
    "pendingProfileCompletion",
    "pending_profile_completion",
    "pendingApproval",
    "pending_approval",
    "pendingKyc",
    "pending_kyc",
    "pending",
  ]);
  if ((toNumber(pendingVerification) || 0) > 0) {
    items.push({
      title: "ID Verification",
      message: `${formatCount(pendingVerification)} profiles awaiting verification.`,
      action: "Verify Now",
      tone: "warning",
      icon: "🛡️",
      actionColor: "#F99C07",
    });
  }

  if (!items.length) {
    return [
      {
        title: "All Clear",
        message: "No urgent alerts based on current dashboard metrics.",
        action: "View Details",
        tone: "info",
        icon: "🛡️",
        actionColor: "#61013B",
      },
    ];
  }

  return items.slice(0, 3);
};

const bottomCards = [
  {
    title: "Total Providers",
    value: "—",
    subLabel: "All providers",
    meta: "—",
    metaLabel: "Rated Coverage",
    accent: "#7a0051",
    icon: "User",
  },
  {
    title: "Rated Providers",
    value: "—",
    subLabel: "Providers with ratings",
    meta: "—",
    metaLabel: "Avg Rating",
    accent: "#570161",
    icon: "Home",
  },
  {
    title: "Average Rating",
    value: "—",
    subLabel: "Across rated providers",
    meta: "—",
    metaLabel: "Rated Providers",
    accent: "#6b7280",
    icon: "Car",
  },
];

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const buildMonthOptions = (count = 4) => {
  const options = [];
  const date = new Date();
  for (let i = 0; i < count; i += 1) {
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    const label = `${monthNames[monthIndex]} ${year}`;
    const value = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
    options.push({ label, value });
    date.setMonth(monthIndex - 1);
  }
  return options;
};

const monthOptions = buildMonthOptions(4);

const getFirstValue = (source, keys) => {
  if (!source) return null;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] != null) {
      return source[key];
    }
  }
  return null;
};

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatCurrency = (value, currencyLabel) => {
  const num = toNumber(value);
  if (num === null) return value == null ? "—" : String(value);
  return formatCurrencyValue(num, currencyLabel);
};

const formatCount = (value) => {
  const num = toNumber(value);
  if (num === null) return value == null ? "—" : String(value);
  return num.toLocaleString();
};

const formatDelta = (value) => {
  const num = toNumber(value);
  if (num === null) return value == null ? "—" : String(value);
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toLocaleString()}`;
};

const formatPercentage = (value) => {
  const num = toNumber(value);
  if (num === null) return "—";
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toFixed(2)}%`;
};

const formatRating = (value) => {
  const num = toNumber(value);
  if (num === null) return "—";
  return num.toFixed(2);
};

const getMetricValue = (metric) => {
  if (metric == null) return null;
  if (typeof metric === "object" && metric.value != null) return metric.value;
  return metric;
};

const getMetricChange = (metric) => {
  if (!metric || typeof metric !== "object") return null;
  return metric.percentageChange ?? metric.percentChange ?? null;
};


const normalizeRevenueSeries = (payload) => {
  if (!payload) return [];

  if (Array.isArray(payload?.labels) && Array.isArray(payload?.values)) {
    return payload.labels.map((label, index) => ({
      label: String(label),
      revenue: toNumber(payload.values[index]) ?? 0,
    }));
  }

  const candidates =
    payload?.data ||
    payload?.items ||
    payload?.results ||
    payload?.series ||
    payload?.points ||
    payload?.chart ||
    payload;

  if (!Array.isArray(candidates)) return [];

  return candidates.map((item, index) => {
    if (typeof item === "number") {
      return { label: String(index + 1), revenue: item };
    }

    const label =
      getFirstValue(item, ["label", "day", "date", "period", "name", "x"]) ??
      String(index + 1);
    const value = getFirstValue(item, [
      "revenue",
      "amount",
      "totalRevenue",
      "total_revenue",
      "value",
      "y",
    ]);

    return { label: String(label), revenue: toNumber(value) ?? 0 };
  });
};

const applyOverviewToKpis = (cards, overview, currencyLabel) => {
  const source = overview?.totals ?? overview;
  if (!source) return cards;
  return cards.map((card) => {
    if (card.title === "Total Revenue") {
      const metric =
        source.totalRevenue ||
        source.total_revenue ||
        source.revenue ||
        source.totalRevenueAmount ||
        source.total_revenue_amount;
      const value = getMetricValue(metric);
      const change = getMetricChange(metric);
      return value == null
        ? card
        : {
            ...card,
            value: formatCurrency(value, currencyLabel),
            change: change == null ? card.change : formatPercentage(change),
          };
    }
    if (card.title === "Total Bookings") {
      const metric =
        source.totalBookings ||
        source.total_bookings ||
        source.bookings ||
        source.totalBookingsCount ||
        source.total_bookings_count;
      const value = getMetricValue(metric);
      const change = getMetricChange(metric);
      return value == null
        ? card
        : {
            ...card,
            value: formatCount(value),
            change: change == null ? card.change : formatPercentage(change),
          };
    }
    if (card.title === "Active Users") {
      const metric =
        source.totalActiveUsers ||
        source.activeUsers ||
        source.active_users ||
        source.usersActive ||
        source.activeUserCount ||
        source.active_users_count;
      const value = getMetricValue(metric);
      const change = getMetricChange(metric);
      const breakdown = metric?.breakdown || metric?.break_down || {};
      const existing = breakdown.existingUsers || breakdown.existing_users;
      const fresh = breakdown.newUsers || breakdown.new_users;
      const existingValue = getMetricValue(existing);
      const newValue = getMetricValue(fresh);
      const total = (toNumber(existingValue) || 0) + (toNumber(newValue) || 0);
      const segments =
        existingValue != null || newValue != null
          ? [
              {
                label: "Returning User",
                color: "#570033",
                width: `${total ? (toNumber(existingValue) / total) * 100 : 0}%`,
              },
              {
                label: "New User",
                color: "#9ca3af",
                width: `${total ? (toNumber(newValue) / total) * 100 : 0}%`,
              },
            ]
          : card.segments;

      return value == null
        ? card
        : {
            ...card,
            value: formatCount(value),
            change: change == null ? card.change : formatPercentage(change),
            segments,
          };
    }
    if (card.title === "New Registrations") {
      const metric =
        source.totalNewRegistrations ||
        source.newRegistrations ||
        source.new_registrations ||
        source.newUsers ||
        source.new_users ||
        source.newUserRegistrations ||
        source.new_user_registrations;
      const value = getMetricValue(metric);
      const change = getMetricChange(metric);
      return value == null
        ? card
        : {
            ...card,
            value: formatDelta(value),
            change: change == null ? card.change : formatPercentage(change),
          };
    }
    return card;
  });
};

const applyProvidersToBottomCards = (cards, providers) => {
  const source = providers?.totals ?? providers?.metrics ?? providers;
  if (!source) return cards;

  const totalProviders = getFirstValue(source, [
    "totalProviders",
    "total_providers",
    "providers",
    "total",
  ]);
  const averageRating = getFirstValue(source, [
    "averageProvidersRating",
    "average_providers_rating",
    "avgProvidersRating",
    "avgProviderRating",
    "averageRating",
    "avgRating",
  ]);
  const ratedProviders = getFirstValue(source, [
    "ratedProvidersCount",
    "rated_providers_count",
    "ratedProviders",
    "rated_count",
  ]);

  const totalNumber = toNumber(totalProviders) || 0;
  const ratedNumber = toNumber(ratedProviders) || 0;
  const coverage =
    totalNumber > 0 ? `${((ratedNumber / totalNumber) * 100).toFixed(2)}%` : "—";

  return cards.map((card) => {
    if (card.title === "Total Providers") {
      return {
        ...card,
        value: totalProviders == null ? card.value : formatCount(totalProviders),
        meta: coverage,
      };
    }
    if (card.title === "Rated Providers") {
      return {
        ...card,
        value: ratedProviders == null ? card.value : formatCount(ratedProviders),
        meta: averageRating == null ? card.meta : formatRating(averageRating),
      };
    }
    if (card.title === "Average Rating") {
      return {
        ...card,
        value: averageRating == null ? card.value : formatRating(averageRating),
        meta: ratedProviders == null ? card.meta : formatCount(ratedProviders),
      };
    }

    return card;
  });
};

function BottomStatCard({ title, value, subLabel, meta, metaLabel, accent, icon }) {
  const iconMap = {
    User: UserRound,
    Home,
    Car,
  };
  const Icon = iconMap[icon] || UserRound;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div
          className="h-11 w-11 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${accent}1A`, color: accent }}
        >
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
      </div>

      <div className="mt-5 flex items-end justify-between">
        <div>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          <p className="text-md font-medium text-gray-500 mt-1">{subLabel}</p>
        </div>
        <div className="text-right">
          <p className="text-md font-semibold text-[#008000]" >
            {meta}
          </p>
          <p className="text-md font-medium text-gray-500 mt-1">{metaLabel}</p>
        </div>
      </div>
    </div>
  );
}

function UrgentCard({ title, message, action, tone, icon, actionColor }) {
  const toneStyles = {
    danger: "border-red-500 bg-red-50 text-red-600",
    warning: "border-yellow-500 bg-yellow-50 text-yellow-600",
    info: "border-purple-500 bg-purple-50 text-purple-600",
  };
  const cardTone = toneStyles[tone] || toneStyles.info;

  return (
    <div className={`border-l-4 p-4 rounded min-h-[150px] ${cardTone}`}>
      <div className="flex items-start gap-3 ">
        <div className="text-lg">{icon}</div>
        <div className="flex-1 space-y-3 text-gray-900">
          <h4 className="font-semibold">{title}</h4>
          <p className=" md:text-md text-gray-700 mt-1">{message}</p>
          <button
            type="button"
            className="text-sm font-medium mt-2"
            style={{ color: actionColor }}
          >
            {action} →
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, change, segments, progress }) {
  return (
    <div className="bg-white space-y-3 rounded-lg p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-start">
        <h3 className="text-md font-medium text-gray-500">{title}</h3>
        <p className="text-sm text-green-600 bg-[#E6F2E6] p-1 rounded">{change}</p>
      </div>
      <p className="text-2xl font-bold text-gray-800">{value}</p>

      {segments ? (
        <div className="mt-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
            {segments.map((segment) => (
              <div
                key={segment.label}
                className="h-full"
                style={{ width: segment.width, backgroundColor: segment.color }}
              />
            ))}
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {segments.map((segment) => (
              <div key={segment.label} className="flex items-center gap-1.5">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="text-xs text-gray-600">{segment.label}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {progress ? (
        <div className="mt-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#570033] rounded-full"
              style={{ width: progress.width }}
            />
          </div>
          <div className="mt-2">
            <span className="text-xs text-gray-600">{progress.label}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const Shimmer = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-200 ${className}`} />
);

function AdminDashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header Skeleton */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-2">
          <Shimmer className="h-8 w-48 rounded-lg" />
          <Shimmer className="h-4 w-64 rounded-lg" />
        </div>
        <div className="flex items-center gap-3">
          <Shimmer className="h-10 w-32 rounded-lg" />
          <Shimmer className="h-10 w-24 rounded-lg" />
          <Shimmer className="h-10 w-36 rounded-lg" />
        </div>
      </div>

      {/* KPI Cards Grid Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white space-y-4 rounded-lg p-6 shadow-sm border border-gray-100">
            <div className="flex justify-between items-start">
              <Shimmer className="h-5 w-24 rounded" />
              <Shimmer className="h-5 w-12 rounded" />
            </div>
            <Shimmer className="h-8 w-36 rounded" />
            <div className="space-y-2 pt-2">
              <Shimmer className="h-2 w-full rounded-full" />
              <div className="flex gap-2">
                <Shimmer className="h-3 w-12 rounded" />
                <Shimmer className="h-3 w-12 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Performance and Urgent Attention Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Performance Skeleton */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 lg:col-span-2 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-3 flex-1">
              <Shimmer className="h-8 w-48 rounded" />
              <Shimmer className="h-4 w-5/6 rounded" />
              <div className="flex gap-4">
                <Shimmer className="h-4 w-32 rounded" />
                <Shimmer className="h-4 w-32 rounded" />
              </div>
            </div>
            <Shimmer className="h-10 w-32 rounded-lg" />
          </div>
          <Shimmer className="h-[420px] w-full mt-6 rounded-lg" />
        </div>

        {/* Urgent Attention Skeleton */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 lg:col-span-1 flex flex-col justify-between space-y-4">
          <Shimmer className="h-6 w-36 rounded mb-4" />
          <div className="space-y-4 flex-1 flex flex-col justify-between">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border-l-4 border-gray-200 p-4 rounded bg-gray-50/50 space-y-3 min-h-[150px]">
                <div className="flex gap-3">
                  <Shimmer className="h-5 w-5 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Shimmer className="h-5 w-32 rounded" />
                    <Shimmer className="h-4 w-full rounded" />
                    <Shimmer className="h-4 w-5/6 rounded" />
                    <Shimmer className="h-4 w-20 rounded mt-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Stat Cards Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-5">
            <div className="flex items-center gap-3">
              <Shimmer className="h-11 w-11 rounded-full" />
              <Shimmer className="h-6 w-36 rounded" />
            </div>
            <div className="flex justify-between items-end">
              <div className="space-y-2">
                <Shimmer className="h-8 w-20 rounded" />
                <Shimmer className="h-4 w-28 rounded" />
              </div>
              <div className="space-y-2 text-right">
                <Shimmer className="h-4 w-16 ml-auto rounded" />
                <Shimmer className="h-4 w-24 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminDashboard() {
  const { config } = useAppConfigContext();
  const currencyLabel = config.primaryCurrency;
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeValue, setRangeValue] = useState("Last 7 days");
  const [rangeQuery, setRangeQuery] = useState("");
  const dropdownRef = useRef(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]);
  const [monthQuery, setMonthQuery] = useState("");
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");
  const [financialSummary, setFinancialSummary] = useState(null);
  const [financialSummaryLoading, setFinancialSummaryLoading] = useState(true);
  const [financialSummaryError, setFinancialSummaryError] = useState("");
  const [revenueData, setRevenueData] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState("");
  const [providersMetrics, setProvidersMetrics] = useState(null);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState("");
  const [reportFormat, setReportFormat] = useState("json");
  const [reportDownloading, setReportDownloading] = useState(false);
  const [reportError, setReportError] = useState("");
  const dateRef = useRef(null);
  const filteredMonthOptions = monthQuery.trim()
    ? monthOptions.filter((option) =>
        option.label.toLowerCase().includes(monthQuery.trim().toLowerCase())
      )
    : monthOptions;

  useEffect(() => {
    const handleOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setRangeOpen(false);
      }
      if (dateRef.current && !dateRef.current.contains(event.target)) {
        setDateOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    let isActive = true;
    const fetchOverview = async () => {
      setOverviewLoading(true);
      setOverviewError("");
      try {
        const data = await adminService.getDashboardOverview(selectedMonth?.value);
        if (!isActive) return;
        setOverviewData(data?.data ?? data);
      } catch (error) {
        if (!isActive) return;
        setOverviewError(error?.message || "Failed to load overview metrics.");
      } finally {
        if (isActive) setOverviewLoading(false);
      }
    };

    fetchOverview();
    return () => {
      isActive = false;
    };
  }, [selectedMonth?.value]);

  useEffect(() => {
    let isActive = true;

    const fetchFinancialSummary = async () => {
      setFinancialSummaryLoading(true);
      setFinancialSummaryError("");
      try {
        const data = await adminService.getFinancialSummary();
        if (!isActive) return;
        setFinancialSummary(data?.data ?? data);
      } catch (error) {
        if (!isActive) return;
        setFinancialSummaryError(error?.message || "Failed to load financial summary.");
      } finally {
        if (isActive) setFinancialSummaryLoading(false);
      }
    };

    fetchFinancialSummary();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const fetchProvidersMetrics = async () => {
      setProvidersLoading(true);
      setProvidersError("");
      try {
        const data = await adminService.getDashboardProviders();
        if (!isActive) return;
        setProvidersMetrics(data?.data ?? data);
      } catch (error) {
        if (!isActive) return;
        setProvidersError(error?.message || "Failed to load providers metrics.");
      } finally {
        if (isActive) setProvidersLoading(false);
      }
    };

    fetchProvidersMetrics();
    return () => {
      isActive = false;
    };
  }, []);

  const rangeOptions = [
    "Last 7 days",
    "Last 1 month",
    "Last 3 months",
    "Last 1 year",
  ];
  const filteredRangeOptions = rangeQuery.trim()
    ? rangeOptions.filter((option) =>
        option.toLowerCase().includes(rangeQuery.trim().toLowerCase())
      )
    : rangeOptions;

  const rangeDaysMap = {
    "Last 7 days": 7,
    "Last 1 month": 30,
    "Last 3 months": 90,
    "Last 1 year": 365,
  };

  const rangeDays = rangeDaysMap[rangeValue] ?? 7;

  useEffect(() => {
    let isActive = true;

    const fetchRevenue = async () => {
      setRevenueLoading(true);
      setRevenueError("");
      try {
        const data = await adminService.getRevenuePerformance(rangeDays);
        if (!isActive) return;
        const series = normalizeRevenueSeries(data?.data ?? data);
        if (!series.length) {
          setRevenueError("No revenue data returned.");
        }
        setRevenueData(series);
      } catch (error) {
        if (!isActive) return;
        setRevenueError(error?.message || "Failed to load revenue performance.");
        setRevenueData([]);
      } finally {
        if (isActive) setRevenueLoading(false);
      }
    };

    fetchRevenue();
    return () => {
      isActive = false;
    };
  }, [rangeDays]);

  const resolvedKpiCards = applyOverviewToKpis(kpiCards, overviewData, currencyLabel);
  const financialSource = financialSummary?.totals ?? financialSummary;
  const financialTotalRevenue = getFirstValue(financialSource, [
    "totalRevenue",
    "total_revenue",
    "revenue",
    "totalRevenueAmount",
    "total_revenue_amount",
  ]);
  const financialNetProfit = getFirstValue(financialSource, [
    "totalNetProfit",
    "total_net_profit",
    "netProfit",
    "net_profit",
    "profit",
  ]);
  const revenueTotal = revenueData.reduce(
    (sum, item) => sum + (toNumber(item.revenue) || 0),
    0
  );
  const revenueBarSize =
    revenueData.length >= 120
      ? 4
      : revenueData.length >= 60
        ? 6
        : revenueData.length >= 30
          ? 8
          : revenueData.length >= 14
            ? 14
            : 30;
  const urgentItems = buildUrgentItems({
    overviewData,
    providersMetrics,
    isLoading: overviewLoading || providersLoading,
  });
  const resolvedBottomCards = applyProvidersToBottomCards(bottomCards, providersMetrics);

  const handleDownloadReport = async () => {
    setReportDownloading(true);
    setReportError("");
    try {
      const fileBlob = await adminService.downloadDashboardReport({
        month: selectedMonth?.value,
        format: reportFormat,
      });
      const blob = fileBlob instanceof Blob ? fileBlob : new Blob([fileBlob]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const extension = reportFormat === "csv" ? "csv" : "json";
      const filename = `admin-dashboard-report-${selectedMonth?.value || "current"}.${extension}`;
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setReportError(error?.message || "Failed to download report.");
    } finally {
      setReportDownloading(false);
    }
  };

  const isInitialLoading =
    (overviewLoading && !overviewData) ||
    (financialSummaryLoading && !financialSummary) ||
    (providersLoading && !providersMetrics) ||
    (revenueLoading && revenueData.length === 0 && !revenueError);

  if (isInitialLoading) {
    return (
      <AdminLayout>
        <AdminDashboardSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard Overview</h1>
            <p className="text-sm text-gray-600">Welcome back, Here is what's happening.</p>
            {overviewError ? (
              <p className="text-xs text-red-600 mt-1">{overviewError}</p>
            ) : overviewLoading ? (
              <p className="text-xs text-gray-500 mt-1">Loading overview metrics...</p>
            ) : null}
            {reportError ? (
              <p className="text-xs text-red-600 mt-1">{reportError}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative" ref={dateRef}>
              <button
                type="button"
                onClick={() => setDateOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm"
              >
                <svg
                  className="h-4 w-4 text-gray-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                <span>{selectedMonth?.label}</span>
                <svg
                  className={`h-4 w-4 text-gray-500 transition-transform ${dateOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M5.5 7.5l4.5 4.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              </button>
              {dateOpen && (
                <div className="absolute right-0 top-full z-20 mt-2 w-40 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
                  <div className="px-2 pb-2">
                    <input
                      type="text"
                      value={monthQuery}
                      onChange={(e) => setMonthQuery(e.target.value)}
                      placeholder="Search month"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                    />
                  </div>
                  <div className="max-h-48 overflow-auto">
                    {filteredMonthOptions.length ? (
                      filteredMonthOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            setSelectedMonth(option);
                            setDateOpen(false);
                            setMonthQuery("");
                          }}
                          className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                            option.value === selectedMonth?.value
                              ? "bg-gray-50 text-gray-900"
                              : ""
                          }`}
                        >
                          {option.label}
                        </button>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-gray-500">No results found</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              <input
                list="admin-dashboard-report-format"
                value={reportFormat}
                onChange={(event) => setReportFormat(event.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                placeholder="Format"
              />
              <datalist id="admin-dashboard-report-format">
                <option value="json" />
                <option value="csv" />
              </datalist>
            </div>
            <button
              type="button"
              onClick={handleDownloadReport}
              disabled={reportDownloading}
              className="inline-flex items-center gap-2 rounded-lg bg-[#570033] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#61013B] disabled:opacity-60"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              {reportDownloading ? "Downloading..." : "Download Report"}
            </button>
          </div>
        </div>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {resolvedKpiCards.map((card) => (
            <KpiCard key={card.title} {...card} />
          ))}
        </div>

        {/* Revenue Performance and Urgent Attention */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Performance */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 lg:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-3">
                <h3 className="text-xl md:text-3xl font-semibold text-gray-900">
                  Revenue Performance
                </h3>
                <p className="text-md font-medium text-gray-600 ">
                  Revenue streams from Accommodation, Trips and Ride
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <span>
                    Total Revenue:{" "}
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(financialTotalRevenue, currencyLabel)}
                    </span>
                  </span>
                  <span>
                    Net Profit:{" "}
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(financialNetProfit, currencyLabel)}
                    </span>
                  </span>
                </div>
                {financialSummaryError ? (
                  <p className="text-xs text-red-600">{financialSummaryError}</p>
                ) : financialSummaryLoading ? (
                  <p className="text-xs text-gray-500">Loading financial summary...</p>
                ) : null}
                {revenueError ? (
                  <p className="text-xs text-red-600">{revenueError}</p>
                ) : revenueLoading ? (
                  <p className="text-xs text-gray-500">Loading revenue performance...</p>
                ) : null}
              </div>
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setRangeOpen((prev) => !prev)}
                  className="inline-flex items-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                >
                  <span>{rangeValue}</span>
                  <svg
                    className={`h-4 w-4 text-gray-500 transition-transform ${
                      rangeOpen ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M5.5 7.5l4.5 4.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  </svg>
                </button>
                {rangeOpen && (
                  <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
                    <div className="px-2 pb-2">
                      <input
                        type="text"
                        value={rangeQuery}
                        onChange={(e) => setRangeQuery(e.target.value)}
                        placeholder="Search range"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                      />
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {filteredRangeOptions.length ? (
                        filteredRangeOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              setRangeValue(option);
                              setRangeOpen(false);
                              setRangeQuery("");
                            }}
                            className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                              option === rangeValue ? "bg-gray-50 text-gray-900" : ""
                            }`}
                          >
                            {option}
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-2 text-sm text-gray-500">No results found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-gray-200 px-3 pb-4 pt-2">
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={revenueData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#d1d5db" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="#6b7280"
                    fontSize={14}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    minTickGap={18}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      padding: "10px 12px",
                    }}
                    labelStyle={{ color: "#111827", fontWeight: 600, marginBottom: "4px" }}
                    formatter={(value) => [formatCurrency(value, currencyLabel), "Revenue"]}
                  />
                  <Bar
                    dataKey="revenue"
                    fill="#61013BC9"
                    radius={[10, 10, 0, 0]}
                    barSize={revenueBarSize}
                  />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-sm text-gray-600 text-center">
                Total: {formatCurrency(revenueTotal, currencyLabel)}
              </p>
            </div>
          </div>

          {/* Urgent Attention */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 lg:col-span-1 flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Urgent Attention
            </h3>
            <div className="space-y-4 flex-1 flex flex-col justify-between">
              {urgentItems.map((item) => (
                <UrgentCard key={item.title} {...item} />
              ))}
            </div>
          </div>
        </div>

        {/* Revenue Trends */}
        {/* <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Revenue Trends</h3>
              <p className="text-sm text-gray-600">
                Daily and monthly revenue, net profit, and transactions
              </p>
              {revenueTrendsError ? (
                <p className="text-xs text-red-600">{revenueTrendsError}</p>
              ) : revenueTrendsLoading ? (
                <p className="text-xs text-gray-500">Loading revenue trends...</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-[#61013B]" />
                Revenue
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-[#16A34A]" />
                Net Profit
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-[#4F46E5]" />
                Transactions
              </span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-800 mb-3">Last 7 Days</p>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trendsDaily}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    minTickGap={16}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      padding: "10px 12px",
                    }}
                    labelStyle={{ color: "#111827", fontWeight: 600, marginBottom: "4px" }}
                    formatter={(value, name) => {
                      if (name === "transactions") {
                        return [formatCount(value), "Transactions"];
                      }
                      if (name === "netProfit") {
                        return [formatCurrency(value, currencyLabel), "Net Profit"];
                      }
                      return [formatCurrency(value, currencyLabel), "Revenue"];
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#61013B"
                    strokeWidth={2}
                    dot={false}
                    yAxisId="left"
                  />
                  <Line
                    type="monotone"
                    dataKey="netProfit"
                    stroke="#16A34A"
                    strokeWidth={2}
                    dot={false}
                    yAxisId="left"
                  />
                  <Line
                    type="monotone"
                    dataKey="transactions"
                    stroke="#4F46E5"
                    strokeWidth={2}
                    dot={false}
                    yAxisId="right"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-800 mb-3">Last 12 Months</p>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trendsMonthly}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    minTickGap={16}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      padding: "10px 12px",
                    }}
                    labelStyle={{ color: "#111827", fontWeight: 600, marginBottom: "4px" }}
                    formatter={(value, name) => {
                      if (name === "transactions") {
                        return [formatCount(value), "Transactions"];
                      }
                      if (name === "netProfit") {
                        return [formatCurrency(value, currencyLabel), "Net Profit"];
                      }
                      return [formatCurrency(value, currencyLabel), "Revenue"];
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#61013B"
                    strokeWidth={2}
                    dot={false}
                    yAxisId="left"
                  />
                  <Line
                    type="monotone"
                    dataKey="netProfit"
                    stroke="#16A34A"
                    strokeWidth={2}
                    dot={false}
                    yAxisId="left"
                  />
                  <Line
                    type="monotone"
                    dataKey="transactions"
                    stroke="#4F46E5"
                    strokeWidth={2}
                    dot={false}
                    yAxisId="right"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        */}

        {/* Transactions */}
        {/* <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Transactions</h3>
              <p className="text-sm text-gray-600">
                All booking transactions with search and sorting
              </p>
              {transactionsError ? (
                <p className="text-xs text-red-600">{transactionsError}</p>
              ) : transactionsLoading ? (
                <p className="text-xs text-gray-500">Loading transactions...</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <form onSubmit={handleTransactionsSearch} className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={transactionsSearchInput}
                    onChange={(event) => setTransactionsSearchInput(event.target.value)}
                    placeholder="Search transactions"
                    className="w-56 rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                  />
                </div>
                <button
                  type="submit"
                  className="rounded-lg bg-[#570033] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#61013B]"
                >
                  Search
                </button>
              </form>
              <input
                list="admin-dashboard-transactions-sort"
                value={transactionsSortBy}
                onChange={(event) => {
                  setTransactionsPage(1);
                  setTransactionsSortBy(event.target.value);
                }}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                placeholder="Sort"
              />
              <datalist id="admin-dashboard-transactions-sort">
                <option value="createdAt:desc" />
                <option value="createdAt:asc" />
                <option value="amount:desc" />
                <option value="amount:asc" />
              </datalist>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">REFERENCE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">CUSTOMER</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">SERVICE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">DATE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">AMOUNT</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">STATUS</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactionsLoading && transactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-6 text-sm text-gray-500">
                      Loading transactions...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-6 text-sm text-gray-500">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-6">
                        <p className="text-sm font-semibold text-gray-800">{transaction.id}</p>
                      </td>
                      <td className="px-6 py-6">
                        <div className="text-sm">
                          <p className="font-semibold text-gray-800">{transaction.userName}</p>
                          <p className="text-gray-500">{transaction.userEmail}</p>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <div className="text-sm text-gray-700 font-medium">{transaction.service}</div>
                      </td>
                      <td className="px-6 py-6">
                        <p className="text-sm text-gray-700">{formatDate(transaction.createdAt)}</p>
                      </td>
                      <td className="px-6 py-6">
                        <p className="text-sm font-semibold text-gray-800">
                          {formatCurrency(transaction.amount, currencyLabel)}
                        </p>
                      </td>
                      <td className="px-6 py-6">
                        <span
                          className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusClass(
                            transaction.status
                          )}`}
                        >
                          {transaction.status || "Unknown"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{transactionsStart}</span> to{" "}
              <span className="font-medium">{transactionsEnd}</span> of{" "}
              <span className="font-medium">
                {transactionsTotal || transactions.length}
              </span>{" "}
              results
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setTransactionsPage((page) => Math.max(1, page - 1))}
                disabled={transactionsPage === 1}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(3, transactionsTotalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setTransactionsPage(page)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        transactionsPage === page
                          ? "bg-[#570033] text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {transactionsTotalPages > 3 ? (
                  <>
                    <span className="px-2 py-1 text-gray-500">...</span>
                    <button
                      type="button"
                      onClick={() => setTransactionsPage(transactionsTotalPages)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        transactionsPage === transactionsTotalPages
                          ? "bg-[#570033] text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {transactionsTotalPages}
                    </button>
                  </>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() =>
                  setTransactionsPage((page) => Math.min(transactionsTotalPages, page + 1))
                }
                disabled={transactionsPage === transactionsTotalPages}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        */}

        {/* Providers Directory */}
        {/* <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Providers Directory</h3>
              <p className="text-sm text-gray-600">
                Search and filter providers by status
              </p>
              {providersDirectoryError ? (
                <p className="text-xs text-red-600">{providersDirectoryError}</p>
              ) : providersDirectoryLoading ? (
                <p className="text-xs text-gray-500">Loading providers directory...</p>
              ) : null}
              {providersInsightsError ? (
                <p className="text-xs text-red-600">{providersInsightsError}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <input
                list="admin-dashboard-providers-format"
                value={providersInsightsFormat}
                onChange={(event) => setProvidersInsightsFormat(event.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                placeholder="Format"
              />
              <datalist id="admin-dashboard-providers-format">
                <option value="json" />
                <option value="csv" />
              </datalist>
              <button
                type="button"
                onClick={handleDownloadProvidersInsights}
                disabled={providersInsightsDownloading}
                className="inline-flex items-center gap-2 rounded-lg bg-[#570033] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#61013B] disabled:opacity-60"
              >
                {providersInsightsDownloading ? "Downloading..." : "Download Insights"}
              </button>
              <form onSubmit={handleProvidersDirectorySearch} className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={providersDirectorySearchInput}
                    onChange={(event) => setProvidersDirectorySearchInput(event.target.value)}
                    placeholder="Search providers"
                    className="w-56 rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                  />
                </div>
                <button
                  type="submit"
                  className="rounded-lg bg-[#570033] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#61013B]"
                >
                  Search
                </button>
              </form>
              <input
                list="admin-dashboard-providers-status"
                value={providersDirectoryStatus}
                onChange={(event) => {
                  setProvidersDirectoryPage(1);
                  setProvidersDirectoryStatus(event.target.value);
                }}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-[#570033] focus:outline-none focus:ring-2 focus:ring-[#570033]/20"
                placeholder="All statuses"
              />
              <datalist id="admin-dashboard-providers-status">
                <option value="" />
                <option value="active" />
                <option value="inactive" />
                <option value="pending" />
                <option value="completed" />
              </datalist>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">PROVIDER</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">CONTACT</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">SERVICE</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">LOCATION</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">RATING</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">BOOKINGS</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">STATUS</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">DETAILS</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {providersDirectoryLoading && providersDirectory.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-6 text-sm text-gray-500">
                      Loading providers...
                    </td>
                  </tr>
                ) : providersDirectory.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-6 text-sm text-gray-500">
                      No providers found.
                    </td>
                  </tr>
                ) : (
                  providersDirectory.map((provider) => (
                    <tr key={provider.id} className="hover:bg-gray-50">
                      <td className="px-6 py-6">
                        <p className="text-sm font-semibold text-gray-800">{provider.name}</p>
                      </td>
                      <td className="px-6 py-6">
                        <div className="text-sm">
                          <p className="text-gray-700">{provider.email}</p>
                          <p className="text-gray-500">{provider.phone}</p>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <span className="text-sm text-gray-700 font-medium">{provider.service}</span>
                      </td>
                      <td className="px-6 py-6">
                        <span className="text-sm text-gray-700">{provider.location}</span>
                      </td>
                      <td className="px-6 py-6">
                        <span className="text-sm text-gray-700">
                          {provider.rating ?? "—"}
                        </span>
                      </td>
                      <td className="px-6 py-6">
                        <span className="text-sm text-gray-700">
                          {provider.completedBookings == null
                            ? "—"
                            : formatCount(provider.completedBookings)}
                        </span>
                      </td>
                      <td className="px-6 py-6">
                        <span
                          className={`px-3 py-1 rounded-lg text-xs font-medium ${getProviderStatusClass(
                            provider.status
                          )}`}
                        >
                          {provider.status || "Unknown"}
                        </span>
                      </td>
                      <td className="px-6 py-6">
                        <button
                          type="button"
                          onClick={() => handleOpenProviderDetail(provider.providerId)}
                          disabled={!provider.providerId}
                          className="text-sm font-semibold text-[#570033] disabled:opacity-40"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{providersDirectoryStart}</span> to{" "}
              <span className="font-medium">{providersDirectoryEnd}</span> of{" "}
              <span className="font-medium">
                {providersDirectoryTotal || providersDirectory.length}
              </span>{" "}
              results
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setProvidersDirectoryPage((page) => Math.max(1, page - 1))}
                disabled={providersDirectoryPage === 1}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(3, providersDirectoryTotalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setProvidersDirectoryPage(page)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        providersDirectoryPage === page
                          ? "bg-[#570033] text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {providersDirectoryTotalPages > 3 ? (
                  <>
                    <span className="px-2 py-1 text-gray-500">...</span>
                    <button
                      type="button"
                      onClick={() => setProvidersDirectoryPage(providersDirectoryTotalPages)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        providersDirectoryPage === providersDirectoryTotalPages
                          ? "bg-[#570033] text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {providersDirectoryTotalPages}
                    </button>
                  </>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() =>
                  setProvidersDirectoryPage((page) =>
                    Math.min(providersDirectoryTotalPages, page + 1)
                  )
                }
                disabled={providersDirectoryPage === providersDirectoryTotalPages}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        */}

        {/* Bottom Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {resolvedBottomCards.map((card) => (
            <BottomStatCard key={card.title} {...card} />
          ))}
        </div>
        {providersError ? (
          <p className="text-xs text-red-600">{providersError}</p>
        ) : providersLoading ? (
          <p className="text-xs text-gray-500">Loading providers metrics...</p>
        ) : null}
      </div>
    </AdminLayout>
  );
}

export default AdminDashboard;
